// IdP Settings page — allows tenant admin to switch the authentication provider
// and monitor identity rebind progress after a switch.
// Admin-only (requireTenantAdmin enforced by backend; frontend checks isAdmin for guard).

import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { useCurrentUser } from '@/lib/hooks/use-current-user'
import { platformApi, type IdPProvider, type SwitchIdPRequest } from '@/lib/api/platform'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { FormSkeleton } from '@/components/shared/LoadingSkeleton'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const MAGIC_LINK_SLUG = 'magic_link'
const POLL_INTERVAL_MS = 30_000

export function IdPSettingsPage() {
  const { t } = useTranslation(['platform', 'common'])
  const api = useApi()
  const { slug } = useTenant()
  const currentUser = useCurrentUser()
  const pApi = platformApi(api)

  // Form state
  const [selectedProviderId, setSelectedProviderId] = useState<string>('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [tenantIdentifier, setTenantIdentifier] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [switchSuccess, setSwitchSuccess] = useState<{ message: string } | null>(null)

  // Polling ref — cleared on unmount
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // ---- Queries ----

  const {
    data: subscription,
    isLoading: subLoading,
    error: subError,
    refetch: refetchSub,
  } = useQuery({
    queryKey: queryKeys.platform.subscription(slug),
    queryFn: () => pApi.getSubscription(),
    retry: false,
  })

  const {
    data: providers,
    isLoading: providersLoading,
    error: providersError,
    refetch: refetchProviders,
  } = useQuery({
    queryKey: queryKeys.platform.idpProviders(slug),
    queryFn: () => pApi.getIdPProviders(),
    retry: false,
  })

  const {
    data: rebindStatus,
    refetch: refetchRebindStatus,
  } = useQuery({
    queryKey: queryKeys.platform.rebindStatus(slug),
    queryFn: () => pApi.getRebindStatus(),
    retry: false,
    // Only fetch after first successful switch or always (to show current state)
    enabled: true,
  })

  // ---- Polling ----

  useEffect(() => {
    if (rebindStatus && rebindStatus.pending > 0) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => {
          void refetchRebindStatus()
        }, POLL_INTERVAL_MS)
      }
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [rebindStatus, refetchRebindStatus])

  // ---- Derived values ----

  const selectedProvider: IdPProvider | undefined = providers?.find(
    (p) => p.id === selectedProviderId,
  )
  const isMagicLink = selectedProvider?.slug === MAGIC_LINK_SLUG
  const isOidc = selectedProvider != null && !isMagicLink
  const showTenantIdentifier = isOidc && (selectedProvider?.requires_tenant_identifier ?? false)

  // ---- Mutation ----

  const switchMutation = useMutation({
    mutationFn: (data: SwitchIdPRequest) => pApi.switchIdP(data),
    onSuccess: (resp) => {
      setSwitchSuccess({
        message: t('platform:idp.switchSuccess', { count: resp.users_pending_rebind }),
      })
      setFormError(null)
      // Reload the page after a short delay so the user sees the success message.
      // A full reload is required because TenantShell holds authConfig in React
      // state — query invalidation alone cannot update it. Without a reload, the
      // old auth mode remains active and the user is directed to the wrong login
      // flow (e.g. magic link login page after switching back to OIDC).
      setTimeout(() => {
        window.location.reload()
      }, 1500)
    },
    onError: (err) => {
      setFormError(err instanceof ApiRequestError ? err.message : t('common:errors.unexpected'))
    },
  })

  // ---- Handlers ----

  function handleSubmit() {
    setFormError(null)
    setSwitchSuccess(null)

    if (!selectedProviderId) {
      setFormError(t('platform:idp.providerLabel') + ' is required')
      return
    }

    if (isOidc && !clientId.trim()) {
      setFormError(t('platform:idp.clientIdLabel') + ' is required for OIDC providers')
      return
    }

    const payload: SwitchIdPRequest = {
      idp_provider_id: selectedProviderId,
    }
    if (isOidc) {
      payload.client_id = clientId.trim()
      if (clientSecret.trim()) payload.client_secret = clientSecret.trim()
      if (showTenantIdentifier && tenantIdentifier.trim()) {
        payload.tenant_identifier = tenantIdentifier.trim()
      }
    }

    switchMutation.mutate(payload)
  }

  // ---- Render ----

  if (!currentUser.is_tenant_admin) return null

  if (subLoading || providersLoading) return <FormSkeleton />

  if (subError) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('platform:idp.title') })}
        technicalDetail={subError instanceof ApiRequestError ? subError.message : undefined}
        onRetry={() => refetchSub()}
      />
    )
  }

  if (providersError) {
    return (
      <ErrorState
        message={t('common:errors.loadFailed', { resource: t('platform:idp.title') })}
        technicalDetail={
          providersError instanceof ApiRequestError ? providersError.message : undefined
        }
        onRetry={() => refetchProviders()}
      />
    )
  }

  const showRebindSection =
    switchSuccess != null || (rebindStatus != null && rebindStatus.pending > 0)

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('platform:idp.title')}</h1>
      </div>

      {/* Section 1: Current authentication */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform:idp.currentProvider')}</CardTitle>
        </CardHeader>
        <CardContent>
          {subscription?.idp_provider_name ? (
            <p className="text-sm">{subscription.idp_provider_name}</p>
          ) : (
            <p className="text-sm text-muted-foreground">
              {t('platform:subscription.idpNotConfigured')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Section 2: Switch authentication provider */}
      <Card>
        <CardHeader>
          <CardTitle>{t('platform:idp.switchTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Warning */}
          <p className="text-sm text-muted-foreground">{t('platform:idp.switchWarning')}</p>

          {/* Provider picker */}
          <div className="space-y-2">
            <Label htmlFor="idp-provider">{t('platform:idp.providerLabel')}</Label>
            <Select
              value={selectedProviderId}
              onValueChange={(val) => {
                setSelectedProviderId(val)
                setFormError(null)
                setSwitchSuccess(null)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a provider..." />
              </SelectTrigger>
              <SelectContent>
                {(providers ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* OIDC-only fields */}
          {isOidc && (
            <>
              <div className="space-y-2">
                <Label htmlFor="client-id">{t('platform:idp.clientIdLabel')}</Label>
                <Input
                  id="client-id"
                  value={clientId}
                  onChange={(e) => setClientId(e.target.value)}
                  autoComplete="off"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="client-secret">{t('platform:idp.clientSecretLabel')}</Label>
                <Input
                  id="client-secret"
                  type="password"
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={t('platform:idp.clientSecretPlaceholder')}
                  autoComplete="new-password"
                />
              </div>

              {showTenantIdentifier && (
                <div className="space-y-2">
                  <Label htmlFor="tenant-identifier">
                    {t('platform:idp.tenantIdentifierLabel')}
                  </Label>
                  <Input
                    id="tenant-identifier"
                    value={tenantIdentifier}
                    onChange={(e) => setTenantIdentifier(e.target.value)}
                    autoComplete="off"
                  />
                </div>
              )}
            </>
          )}

          {/* Inline form error */}
          {formError && <p className="text-sm text-destructive">{formError}</p>}

          {/* Success message */}
          {switchSuccess && (
            <p className="text-sm text-green-600 dark:text-green-400">{switchSuccess.message}</p>
          )}

          <Button
            onClick={handleSubmit}
            disabled={switchMutation.isPending || !selectedProviderId}
          >
            {switchMutation.isPending
              ? t('platform:idp.switching')
              : t('platform:idp.switchButton')}
          </Button>
        </CardContent>
      </Card>

      {/* Section 3: Rebind progress */}
      {showRebindSection && rebindStatus != null && (
        <Card>
          <CardHeader>
            <CardTitle>{t('platform:idp.rebindProgressTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            {rebindStatus.pending === 0 ? (
              <p className="text-sm text-green-600 dark:text-green-400">
                {t('platform:idp.rebindComplete')}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t('platform:idp.rebindPending', {
                  rebound: rebindStatus.rebound,
                  total: rebindStatus.total_users,
                })}
              </p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}
